import React, { FC, MutableRefObject, useCallback, useEffect, useState } from 'react';
import { Button, Icons, WysiwygEditor } from '../../../shared/components';
import { useFetch } from '../../../shared';
import apiRoutes from '../../../../config/api/routes';
import { useParams } from 'react-router';
import { UrlParams } from '../../../shared/types';
import { FeedbackTargetAudience } from '../FeedbackTargetAudience';
import { useTranslation } from 'react-i18next';
import { TargetedStudents } from '../../types';
import { LogActionTypes, useDataLogging } from '../../../shared/data-logging';
import FroalaEditor from 'react-froala-wysiwyg';
import { useMutationObservable } from 'src/modules/shared/hooks/useMutationObservable';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { IGroupStudent } from 'src/config/api/models/User';
import { emptyBlockContent } from 'src/config/api/models/Block';

type AddWysiwygFeedbackProps = {
  pageId: string;
  blockId?: string;
  onSuccess: () => void;
  selectedGroupData?: IGroupStudent[];
};

export const NewWysiwygFeedback: FC<AddWysiwygFeedbackProps> = ({
  pageId,
  blockId,
  onSuccess,
  selectedGroupData,
}) => {
  const { t } = useTranslation();
  const { bookId, chapterId, trajectoryId } = useParams<UrlParams>();
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const selectedStudent = useAppSelector((state) => state.filters.selectedStudent);
  const [refined, setRefined] = useState(false);
  const [targetedStudents, setTargetedStudents] = useState<TargetedStudents | undefined>();
  const [postFeedbackResponse, postFeedback] = useFetch(
    apiRoutes.addFeedback(
      bookId as string,
      chapterId as string,
      trajectoryId as string,
      pageId
    ),
    {
      method: 'POST',
    }
  );
  const log = useDataLogging();
  const [inputHasContent, setInputHasContent] = useState(false);
  const [editorRef, setEditorRef] = useState<MutableRefObject<FroalaEditor | null>>();
  const onListMutation = useCallback(
    (mutationList) => {
      setInputHasContent(mutationList[0].target.innerHTML !== '' && mutationList[0].target.innerHTML !== emptyBlockContent)
    },
    []
  );
  useMutationObservable(editorRef?.current?.getEditor().el, onListMutation);

  const handleSubmit = useCallback(() => {
    if (selectedGroupData == null) return;
    let studentIds: string[] = [];
    if (selectedStudent === '') {
      if (refined && targetedStudents != null) {
        Object.entries(targetedStudents).forEach((entry) => {
          if (entry[1].value) studentIds.push(entry[0]);
        });
      } else {
        studentIds = selectedGroupData.map((student) => student.id) || [];
      }
    } else {
      studentIds = [selectedStudent || ''];
    }

    log(LogActionTypes.FEEDBACK_TEACHER_CREATE, {
      bookId,
      chapterId,
      trajectoryId,
      blockId,
      isWholeGroup: !refined,
      groupId: selectedGroup,
      studentIds,
      type: 'wysiwyg',
    });

    postFeedback({
      body: {
        description: editorRef?.current?.getEditor().el.innerHTML,
        block_id: blockId,
        group_id: selectedGroup,
        student_ids: studentIds,
        is_whole_group: selectedStudent === '' ? !refined : false,
        type: 'wysiwyg',
      },
    });
  }, [
    editorRef,
    selectedGroup,
    selectedGroupData,
    selectedStudent,
    blockId,
    postFeedback,
    refined,
    targetedStudents,
    log,
    bookId,
    chapterId,
    trajectoryId,
  ]);

  useEffect(() => {
    if (postFeedbackResponse.success) onSuccess();
  }, [postFeedbackResponse, onSuccess]);

  return (
    <div style={{ minHeight: '30rem' }}>
      <WysiwygEditor
        type="feedback"
        id="wysiwyg_feedback"
        readOnly={false}
        value={''}
        onLoadRef={setEditorRef}
      />
      <Button
        icon={Icons.ADD}
        disabled={!inputHasContent}
        onClick={handleSubmit}
      >
        {t('feedback.addFeedback')}
      </Button>
      <FeedbackTargetAudience
        isRefined={refined}
        onChangeRefinement={setRefined}
        targetedStudents={targetedStudents}
        onUpdateTargetedStudents={setTargetedStudents}
      />
    </div>
  );
};
